import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Camera, Loader2, Mic, MicOff, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ISSUE_TEMPLATES = [
  { label: "Compactor jam", title: "Compactor jam", priority: "high" },
  { label: "Bottle counter offline", title: "Bottle counter offline", priority: "high" },
  { label: "POS / register issue", title: "POS / register issue", priority: "high" },
  { label: "Front door / lock", title: "Front door / lock issue", priority: "medium" },
  { label: "Lighting out", title: "Lighting out", priority: "low" },
  { label: "Plumbing / leak", title: "Plumbing / leak", priority: "high" },
  { label: "HVAC / temperature", title: "HVAC / temperature issue", priority: "medium" },
  { label: "Cleaning needed", title: "Cleaning needed", priority: "low" },
];

const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 4 * 1024 * 1024; // 4 MB per photo

export default function NewIssueDialog({ open, onOpenChange, locations, user, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [locationId, setLocationId] = useState(user?.location_id || "");
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [recording, setRecording] = useState(false);
  const fileRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTitle("");
      setDescription("");
      setPriority("medium");
      setPhotos([]);
      setRecording(false);
      try {
        recognitionRef.current?.stop?.();
      } catch (err) {
        // Speech recognition can throw if already stopped; not actionable.
        console.debug("SpeechRecognition stop on dialog close:", err);
      }
      setLocationId(user?.location_id || "");
    }
  }, [open, user]);

  const applyTemplate = (tpl) => {
    setTitle(tpl.title);
    setPriority(tpl.priority);
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_PHOTOS - photos.length;
    if (room <= 0) {
      toast.error(`Maximum ${MAX_PHOTOS} photos`);
      return;
    }
    const accepted = files.slice(0, room);
    accepted.forEach((f) => {
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name}: must be under 4MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPhotos((prev) => [...prev, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, src: reader.result }]);
      reader.readAsDataURL(f);
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const toggleRecording = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser. Try Chrome or Safari.");
      return;
    }
    if (recording) {
      try {
        recognitionRef.current?.stop();
      } catch (err) {
        console.debug("SpeechRecognition stop while recording:", err);
      }
      setRecording(false);
      return;
    }
    const r = new SR();
    r.continuous = true;
    r.interimResults = false;
    r.lang = navigator.language || "en-US";
    let appended = "";
    r.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        appended += (appended || description ? " " : "") + t.trim();
      }
      setDescription((prev) => (prev ? prev + " " + appended : appended).trim());
      appended = "";
    };
    r.onerror = (e) => {
      setRecording(false);
      if (e.error === "not-allowed") toast.error("Microphone permission denied");
      else if (e.error !== "aborted") toast.error("Voice input error: " + e.error);
    };
    r.onend = () => setRecording(false);
    recognitionRef.current = r;
    try {
      r.start();
      setRecording(true);
    } catch {
      toast.error("Couldn't start voice input");
    }
  };

  const submit = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (user?.role === "admin" && !locationId) return toast.error("Pick a location");
    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        priority,
        location_id: locationId,
        photos: photos.map((p) => p.src),
      };
      const { data } = await api.post("/issues", payload);
      toast.success("Issue submitted");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-issue-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">
            New maintenance issue
          </DialogTitle>
          <DialogDescription>
            Tap a template, describe the problem, and attach photos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Templates */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Quick templates</Label>
            <div className="flex flex-wrap gap-2" data-testid="issue-templates">
              {ISSUE_TEMPLATES.map((tpl) => (
                <button
                  key={tpl.label}
                  type="button"
                  onClick={() => applyTemplate(tpl)}
                  data-testid={`tpl-${tpl.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-gray-200 hover:border-[#0055FF] hover:text-[#0055FF] transition-colors"
                >
                  {tpl.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Title</Label>
            <Input
              data-testid="new-issue-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Leaking faucet in bathroom"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Description</Label>
              <button
                type="button"
                onClick={toggleRecording}
                data-testid="voice-toggle"
                className={`text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded transition-colors ${
                  recording
                    ? "bg-red-50 text-red-600 animate-pulse"
                    : "text-gray-600 hover:text-[#0055FF]"
                }`}
                aria-label={recording ? "Stop voice input" : "Start voice input"}
              >
                {recording ? <MicOff size={12} /> : <Mic size={12} />}
                {recording ? "Listening…" : "Voice"}
              </button>
            </div>
            <Textarea
              data-testid="new-issue-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Details that help the maintenance team…"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="new-issue-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {user?.role === "admin" ? (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">
                  Location
                </Label>
                <Select value={locationId} onValueChange={setLocationId}>
                  <SelectTrigger data-testid="new-issue-location">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">
                  Location
                </Label>
                <Input
                  readOnly
                  value={locations.find((l) => l.id === user?.location_id)?.name || "—"}
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">
                Photos {photos.length > 0 && `(${photos.length}/${MAX_PHOTOS})`}
              </Label>
            </div>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p, idx) => (
                  <div key={p.id} className="relative aspect-square overflow-hidden rounded-md border border-gray-200">
                    <img src={p.src} alt={`upload ${idx + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      data-testid={`new-issue-remove-photo-${idx}`}
                      onClick={() => removePhoto(p.id)}
                      className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5"
                      aria-label="Remove photo"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {photos.length < MAX_PHOTOS && (
              <button
                type="button"
                data-testid="new-issue-add-photo"
                onClick={() => fileRef.current?.click()}
                className="w-full border-2 border-dashed border-gray-300 hover:border-[#0055FF] rounded-md p-5 flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-[#0055FF] transition-colors"
              >
                <Camera size={20} />
                <span className="text-sm">
                  {photos.length === 0
                    ? "Tap to attach photos (up to 5)"
                    : `Add another photo (${MAX_PHOTOS - photos.length} left)`}
                </span>
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              capture="environment"
              className="hidden"
              onChange={handleFiles}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="new-issue-cancel">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="new-issue-submit"
            className="bg-[#0055FF] hover:bg-[#0044CC]"
          >
            {submitting ? <Loader2 className="animate-spin" size={14} /> : "Submit issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
